import React, { useMemo } from "react";
import PT from "prop-types";
import * as S from "./styled";
import PriceSlider from "./Price";
import RadiusSlider from "./Radius";
import Checkbox from "_c/Checkbox";
import { useForm, FormContext, Controller } from "react-hook-form";
import Fieldset from "_c/form/Fieldset";
import { AbstractControlledField, AbstractField } from "_c/form/Field";
import Label from "_c/form/Label";
import omit from "lodash/omit";
import moment from "moment";
import Tooltip from "_c/Tooltip";
import { SEARCH_NS as ns } from "@/i18n/ns";
import Calendar from "_c/Calendar";
import { Header, Content, Footer } from "_c/Modals";
import { MASTER_SEARCH_RADIUS } from "@/misc/constants";
import { useSearchUrlGenerator } from "@/hooks/useSearchUrlGenerator";
import { useHistory } from "react-router";
import orderBy from "lodash/orderBy";
import { MASTERS_SEARCH_SHOW_MAP_DRAGGABLE_MARKER } from "@/config";

const sortKeys = [
  "price",
  "-price",
  "-rate",
  "-reviews_count",
  "reviews_count",
  "dist",
];
const SearchFilters = ({ query, params, t, ...props }) => {
  const generateSearchUrl = useSearchUrlGenerator();
  const history = useHistory();
  const specs = useMemo(() => {
    return [{ value: "no", label: t("common:not_selected") }].concat(
      orderBy(
        props.specs.map(({ _id, title }) => ({
          value: _id,
          label: title,
        })),
        "label",
      ),
    );
  }, [props.specs, t]);
  const defaultSpec = useMemo(() => {
    if (!query.spec && !params.spec) return "no";
    return (
      specs.find(({ value }) => value === query.spec || value === params.spec)
        ?.value ?? "no"
    );
  }, [specs, query.spec, params.spec]);
  const sortOptions = useMemo(() => {
    return [{ value: "no", label: t("common:not_selected") }].concat(
      sortKeys.map((key) => ({
        value: key,
        label: t(`${ns}:filters.sort_strategy.${key}`),
      })),
    );
  }, [t]);
  const onSubmit = (form) => {
    const norm = {
      ...omit(form, ["price", "sort"]),
      price_from:
        form.price[1] > 0 || form.price[0] ? form.price[0] : undefined,
      price_to: form.price[1] > 0 ? form.price[1] : undefined,
      sort: !form.sort || form.sort === "no" ? undefined : form.sort,
      date: form.date
        ? moment(form.date[0]).seconds(0).milliseconds(0).format()
        : undefined,
      spec: !form.spec || form.spec === "no" ? undefined : form.spec,
    };
    const finalQuery = {
      ...query,
      ...norm,
      page: 0,
    };
    const searchUrl = generateSearchUrl({
      params: {
        ...params,
        spec: norm.spec,
      },
      query: finalQuery,
    });
    history.push(searchUrl);
    props.onClose();
  };
  const methods = useForm({
    defaultValues: {
      price: [0, 0],
      radius: Number(query.radius) || MASTER_SEARCH_RADIUS,
      children: query.children || false,
      men: query.men || false,
      date: query.date
        ? moment(query.date).seconds(0).milliseconds(0).toDate()
        : undefined,
      women: query.women || false,
      discounts: query.discounts || false,
      model_search: query.model_search || false,
      works_online: query.works_online || false,
      at_home: query.at_home || false,
      the_road: query.the_road || false,
      spec: defaultSpec,
      at_saloon: query.at_saloon || false,
      price: [Number(query.price_from) || 0, Number(query.price_to) || 0],
      sort: query.sort || "no",
    },
  });
  const { handleSubmit, register, control } = methods;
  return (
    <>
      <Header onClick={() => props.onClose()}>{t("common:close")}</Header>
      <FormContext {...methods}>
        <Content
          as="form"
          id="search-filters"
          aria-label={t(`${ns}:filters.form_aria`)}
          onSubmit={handleSubmit(onSubmit)}
        >
          <AbstractField
            id="search-spec"
            Component="select"
            name="spec"
            className="simpo-native-select"
            label={t(`${ns}:filters.spec`)}
            t={t}
          >
            {specs.map(({ value, label }) => (
              <option value={value} key={value}>
                {label}
              </option>
            ))}
          </AbstractField>
          <AbstractField
            Component="select"
            id="search-sort"
            label={t(`${ns}:filters.sort`)}
            className="simpo-native-select"
            name="sort"
            t={t}
          >
            {sortOptions.map(({ value, label }) => (
              <option value={value} key={value}>
                {label}
              </option>
            ))}
          </AbstractField>
          <Fieldset>
            <Label as="legend">{t(`${ns}:filters.loc`)}</Label>
            <Checkbox
              ref={register}
              name="at_saloon"
              id="search-at_saloon"
              label={t(`${ns}:filters.loc_saloon`)}
            />
            <Checkbox
              id="search-the_road"
              ref={register}
              name="the_road"
              label={t(`${ns}:filters.loc_road`)}
            />
            <Checkbox
              name="at_home"
              ref={register}
              id="search-at_home"
              label={t(`${ns}:filters.loc_home`)}
            />
            <Checkbox
              id="search-online"
              ref={register}
              name="works_online"
              label={t(`${ns}:filters.loc_online`)}
            />
          </Fieldset>
          <Fieldset>
            <Label as="legend">{t(`${ns}:filters.work_with`)}</Label>
            <Checkbox
              name="children"
              ref={register}
              id="search-children"
              label={t("common:child_many")}
            />
            <Checkbox
              name="women"
              ref={register}
              id="search-women"
              label={t("common:female_many")}
            />
            <Checkbox
              name="men"
              ref={register}
              id="search-men"
              label={t("common:male_many")}
            />
          </Fieldset>
          <Fieldset>
            <Label as="legend">{t(`${ns}:filters.interest`)}</Label>
            <Checkbox
              name="discounts"
              ref={register}
              id="search-discounts"
              label={t(`${ns}:filters.discounts`)}
            />
            <Checkbox
              name="model_search"
              ref={register}
              id="search-model_search"
              label={t(`${ns}:filters.model_search`)}
            />
          </Fieldset>
          <AbstractControlledField
            ariaLabelForHandle={t(`${ns}:filters.radius`)}
            label={t(`${ns}:filters.radius`)}
            name="radius"
            id="search-radius"
            Component={RadiusSlider}
          />
          <Controller t={t} control={control} name="price" as={PriceSlider} />
          <AbstractControlledField
            Component={Calendar}
            className="flatpickr-input"
            options={{
              altInput: true,
              altFormat: "F j, Y H:i",
              dateFormat: "Y-m-d",
              enableTime: true,
              time_24hr: true,
            }}
            name="date"
            id="search-date"
            label={t(`${ns}:filters.time`)}
          />
          {MASTERS_SEARCH_SHOW_MAP_DRAGGABLE_MARKER && (
            <S.GeoWrap>
              <Label as="span">{t(`${ns}:filters.geo`)}</Label>
              <Tooltip title={t(`${ns}:filters.geo_tip`)}>
                <S.GeoTip as="span" small primary>
                  i
                </S.GeoTip>
              </Tooltip>
            </S.GeoWrap>
          )}
        </Content>
      </FormContext>
      <Footer form="search-filters" type="submit">
        {t(`${ns}:filters.apply`)}
      </Footer>
    </>
  );
};
SearchFilters.propTypes = {
  query: PT.object,
  specs: PT.array,

  t: PT.func.isRequired,
  onClose: PT.func.isRequired,
};

export default SearchFilters;
